import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { useAppBranding } from '../../../config/branding';
import DefaultApiLimit from '../../../config/defaultLimit';
import {
  AccountPayout,
  AccountPayoutRequest,
} from '../../../interface/account-management-payout.interface';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { setNotificationMessage } from '../../../redux/slices/NotificationManagementSlice';
import {
  disableAccountPayoutFilterApplied,
  fetchAccountPayoutFilters,
  fetchPayout,
  setSortData,
  updateAccountPayoutFilters,
} from '../../../redux/slices/PayoutSlice';
import MyButton from '../../../shared-components/button/Button';
import CustomDateRangePicker from '../../../shared-components/custom-date-range/CustomDateRangePicker';
import CustomTable from '../../../shared-components/custom-table/CustomTable';
import ModalBox from '../../../shared-components/modal-box/ModalBox';
import { GetParametersFromCommonFilters } from '../../../shared-functions/CommonFilters';
import ConvertUTCtoDate from '../../../shared-functions/ConvertUTCtoDate';
import { exportCSV, exportPDF } from '../../../shared-functions/ExportFile';
import GetMonthName from '../../../shared-functions/GetMonthName';
import capitalizeFirstLetter from '../../../shared-functions/StringFunctions';
import ToFixedValue from '../../../shared-functions/ToFixedValue';
import accountApi from '../../../utils/api/account';

import PayoutContent from './PayoutContent';

interface ModalInterface {
  title: string;
  header: boolean;
  content: JSX.Element;
  rightCorner: string;
  notification: boolean;
}

/**
 * Payout table component
 * @returns
 */
function Payout() {
  const branding = useAppBranding();
  const { t } = useTranslation('account');
  const dispatch = useAppDispatch();
  const [searchParameters] = useSearchParams();
  const accountCustomId = searchParameters.get('accountCustomId');
  const [openStatus, setOpenStatus] = useState<boolean>(false);
  const [exportLoader, setExportLoader] = useState<boolean>(false);
  const DEFAULT_HIDDEN_COLUMNS: string[] = [];
  const [hiddenColumns, setHiddenColumns] = useState<string[]>(DEFAULT_HIDDEN_COLUMNS);
  const {
    payout,
    limit,
    page,
    totalPages,
    totalResults,
    loading,
    sortBy,
    sortField,
    filters,
    filterListLoading,
    isFilterUpdated,
    revenueTotal,
  } = useAppSelector((state) => state.payout);

  const [dateRange, setDateRange] = useState<{ startDate: string; endDate: string }>({
    startDate: '',
    endDate: '',
  });
  const [modalView, setModalView] = useState<ModalInterface>({
    title: '',
    header: false,
    content: <div />,
    rightCorner: '',
    notification: false,
  });

  const column = [
    {
      label: t('date'),
      accessorKey: 'createdAt' as keyof AccountPayout,
      sortAllow: true,
      /**
       * Rendering time stamp date to UTC
       * @param payoutData
       * @returns
       */
      customData: (payoutData: AccountPayout) => (
        <p>{ConvertUTCtoDate(payoutData?.createdAt || '')}</p>
      ),
    },
    {
      label: t('month'),
      accessorKey: 'month' as keyof AccountPayout,
      /**
       * Rendering month name only
       * @param payout
       */
      customData: (payoutData: AccountPayout) => (
        <p>{GetMonthName({ timestamp: payoutData.periodKey })}</p>
      ),
    },
    {
      label: t('payable'),
      accessorKey: 'revenueSinceLastPayout' as keyof AccountPayout,
      sortAllow: true,
      headingDetails: true,
      headingDescription: <>{t('payableDescription')}</>,
      /**
       * Payable share with two decimal
       * @param payoutData
       * @returns
       */
      customData: (payoutData: AccountPayout) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(payoutData.revenueSinceLastPayout)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('netPayable'),
      accessorKey: 'totalPayable' as keyof AccountPayout,
      sortAllow: true,
      headingDetails: true,
      headingDescription: <>{t('netPayableDescription')}</>,
      /**
       * Net Payable share with two decimal
       * @param payoutData
       * @returns
       */
      customData: (payoutData: AccountPayout) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(payoutData.totalPayable)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('noOfTransaction'),
      accessorKey: 'paidTransactionCount' as keyof AccountPayout,
      sortAllow: true,
      /**
       * Rendering no of transactions counts if its available
       * @param payoutData
       * @returns
       */
      customData: (payoutData: AccountPayout) => <p>{payoutData.paidTransactionCount || 0}</p>,
    },
    {
      label: t('status'),
      accessorKey: 'status' as keyof AccountPayout,
      /**
       * Payment status view in table
       * @param payouts
       * @returns
       */
      customData: (payoutsStatus: AccountPayout) => {
        const statusText = payoutsStatus.status ? capitalizeFirstLetter(payoutsStatus.status) : '';
        return (
          <div className="status-in-payout-table">
            <p className={`${payoutsStatus.status === 'cleared' ? 'success-text' : 'failed'}`}>
              {statusText}
            </p>
          </div>
        );
      },
    },
    {
      label: t('amount'),
      accessorKey: 'totalAmountPaid' as keyof AccountPayout,
      sortAllow: true,
      /**
       * Amount share with two decimal
       * @param payoutData
       * @returns
       */
      customData: (payoutData: AccountPayout) => (
        <div className="value-container">
          <p className="value">{ToFixedValue(payoutData.totalAmountPaid)}</p>
          <span>{branding.text.currency}</span>
        </div>
      ),
    },
    {
      label: t('mode'),
      accessorKey: 'mode' as keyof AccountPayout,
    },
  ];

  /**
   * Clears the date range values
   */
  const clearDateRange = () => {
    setDateRange({
      startDate: '',
      endDate: '',
    });
  };

  /**
   * Export data function (without field filtering)
   */
  const exportDataPdfOrCSV = (type: string) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    if (!accountCustomId) return;
    setExportLoader(true);
    accountApi
      .fetchPayoutExportData(accountCustomId, { ...queryParameters, sortBy, sortField })
      .then((response) => {
        const visibleColumns = column.filter(
          (col) => !hiddenColumns.includes(col.accessorKey as string),
        );

        const headers = [t('sNo'), ...visibleColumns.map((col) => col.label)];

        const rows = [...response.data].map((row: AccountPayout, index: number) => [
          (index + 1).toString(),
          ...visibleColumns.map((col) => {
            const value = row[col.accessorKey];

            switch (col.accessorKey as string) {
              case 'createdAt':
                return ConvertUTCtoDate(row.createdAt || '');

              case 'month':
                return GetMonthName({ timestamp: row.periodKey });

              case 'revenueSinceLastPayout':
              case 'totalPayable':
              case 'totalAmountPaid':
                return `${ToFixedValue(Number(value || 0))} ${branding.text.currency}`;

              case 'paidTransactionCount':
                return (value || 0).toString();

              default:
                return (value ?? '').toString();
            }
          }),
        ]);

        const FILE_NAME = 'account-payout-list';

        setExportLoader(false);
        if (rows.length > 0) {
          if (type === 'csv') {
            exportCSV(headers, rows, `${FILE_NAME}.csv`);
          } else {
            exportPDF(headers, rows, `${FILE_NAME}.pdf`);
          }
        }
      })
      .catch((error) => {
        setExportLoader(false);
        dispatch(
          setNotificationMessage({
            message: error?.response?.data?.message,
            status: true,
            type: 'error',
            code: error?.response?.data?.code,
          }),
        );
      });
  };

  /**
   * Fetch the account details in redus
   */
  const fetchData = (data: AccountPayoutRequest) => {
    if (accountCustomId) {
      dispatch(fetchPayout({ accountCustomId, ...data }));
    }
  };

  // /**
  //  * Handling date filter
  //  * @param field
  //  * @param filterKey
  //  * @param name
  //  * @param value
  //  */
  // const handleDateFilterChange = (
  //   field: 'value1' | 'value2',
  //   filterKey: string,
  //   name: string,
  //   value: string,
  // ) => {
  //   setDateRange((previous) => {
  //     const updatedRange =
  //       field === 'value1' ? { ...previous, startDate: value } : { ...previous, endDate: value };
  //     return updatedRange;
  //   });
  // };

  /**
   * Table pagination function
   * @param value
   */
  const pageAndLimitChange = (value: AccountPayoutRequest) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      ...value,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      sortField,
      sortBy,
    });
  };

  /**
   * Function to refresh data
   */
  const handleRefresh = () => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    fetchData({
      ...queryParameters,
      page,
      limit,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      sortBy,
      sortField,
    });
  };
  /**
   * Handle the filter data clear function
   */
  const handleSortFunction = (sortData: any) => {
    const queryParameters = GetParametersFromCommonFilters(filters);
    dispatch(setSortData(sortData));
    fetchData({
      ...queryParameters,
      sortBy: sortData.sortBy,
      sortField: sortData.sortField,
      page: DefaultApiLimit.page,
      limit,
    });
  };

  /**
   * Handling payout model view
   */
  const handlePayout = () => {
    setOpenStatus(true);
    setModalView({
      title: '',
      header: false,
      content: <PayoutContent setOpenStatus={setOpenStatus} setModalView={setModalView} />,
      rightCorner: '',
      notification: false,
    });
  };

  useEffect(() => {
    if (isFilterUpdated || payout.length === 0 || dateRange.startDate || dateRange.endDate) {
      const queryParameters = GetParametersFromCommonFilters(filters);
      fetchData({
        page: DefaultApiLimit.page,
        limit,
        ...queryParameters,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
      });
      dispatch(disableAccountPayoutFilterApplied());
    }
  }, [filters, dateRange.endDate, dateRange.startDate, accountCustomId]);

  return (
    <div className="payout-list-data">
      <ModalBox
        header={modalView.header}
        title={modalView.title}
        content={modalView.content}
        closeModal={() => setOpenStatus(false)}
        open={openStatus}
        rightCorner={modalView.rightCorner}
        notification={modalView.notification}
      />
      <CustomTable
        version="v2"
        header={column}
        data={payout}
        limit={limit}
        page={page}
        totalPages={totalPages}
        totalResults={totalResults}
        loading={loading}
        serialNo
        sortData={{ sortBy, sortField }}
        handleSortChange={handleSortFunction}
        pageLimitChange={pageAndLimitChange}
        onHiddenColumnsChange={setHiddenColumns}
        actionButton
        filters={filters}
        activeSearchFilter={false}
        height={window.innerHeight >= 700 ? 347 : 352}
        filtersToDispatch={updateAccountPayoutFilters}
        filterListLoading={filterListLoading}
        filterListToDispatch={fetchAccountPayoutFilters}
        sizeChangeButtonRequired
        actionDropdown
        exportData={payout}
        exportLoader={exportLoader}
        exportDataPdfOrCSV={exportDataPdfOrCSV}
        ReferenceCurrentPages={handleRefresh}
        rightTopContent={
          <div className="payout-button">
            <div>
              <CustomDateRangePicker
                startDate={dateRange.startDate}
                endDate={dateRange.endDate}
                onChange={(start, end) =>
                  setDateRange({
                    startDate: start,
                    endDate: end,
                  })
                }
                placeholder="DD-MMM-YYYY - DD-MMM-YYYY"
              />
            </div>
            <div className="amount-container">
              <p>{t('netPayableAmount')}</p>
              <div className="card">
                <p className="icon">{branding.text.currency}</p>
                <div className="line" />
                <p className="amount">{ToFixedValue(revenueTotal?.pendingBalance || 0)}</p>
              </div>
            </div>
            <div className="border-line" />
            <div className="button">
              <MyButton label={t('payout')} buttonType="submit" onClickFunc={handlePayout} />
            </div>
          </div>
        }
        onClearFilterChange={() => {
          clearDateRange();
        }}
      />
    </div>
  );
}

export default Payout;
